import React, { memo, useMemo } from "react";

import {
  faExclamationTriangle,
  faPencil,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";

import { ActionButton } from "components/AgGrid/ActionButton";
import AgGrid from "components/AgGrid/AgGrid";

import { ACTION_COLUMN_NAME } from "constants/aggrid";

import { formatters, getters } from "lib/agGrid";
import {
  dateRenderer,
  dateFormatter,
  dateGetter,
} from "lib/agGrid/columns/date";
import { formatBillingDocumentLabel } from "lib/billingDocument";
import { openEditPaymentModal } from "lib/navigation";

import { useEnsureBillingRunLoaded } from "hooks/useEnsureBillingRunLoaded";
import { useShowBillingDocumentField } from "hooks/useHasPermission";

import {
  getBillingDocumentById,
  getBillingDocumentOwingDollarsById,
  getBusinessById,
  getPayments,
} from "selectors";

function ActionsColumnRenderer({ data }) {
  return (
    <ActionButton
      data-tour="actionEditBusinessPayment"
      type="button"
      onClick={() => openEditPaymentModal(data.id)}
    >
      <FontAwesomeIcon icon={faPencil} />
      &nbsp;Edit
    </ActionButton>
  );
}

const getRowId = params => params.data.id;

const defaultColDef = {
  sortable: true,
  resizable: true,
  enableCellChangeFlash: true,
};

function BillingDocumentColumnRenderer({ value: billingDocumentId }) {
  const billingDocument = useSelector(getBillingDocumentById(billingDocumentId));
  const issuerBusiness = useSelector(
    getBusinessById(billingDocument?.issuerBusinessId),
  );
  const owingDollars = useSelector(
    getBillingDocumentOwingDollarsById(billingDocumentId),
  );

  if (billingDocument == null) {
    return (
      <span title="Not assigned to an invoice">
        <FontAwesomeIcon icon={faExclamationTriangle} color="orange" />
      </span>
    );
  }

  const label = formatBillingDocumentLabel(
    billingDocument,
    issuerBusiness,
    owingDollars,
  );
  return <span title={label}>{label}</span>;
}

const baseColumnsDefs = [
  {
    headerName: "Payment Date",
    field: "paymentDate",
    valueGetter: dateGetter,
    valueFormatter: dateFormatter,
    cellRenderer: dateRenderer,
    // filter: "agDateColumnFilter", TODO Fix aggrid date filtering something to do with GMT and AEDT/AEST and ===
    type: "dateColumn",
    width: 120,
  },
  {
    headerName: "Amount ($)",
    field: "totalAmountCents",
    valueGetter: getters.dollarsGetter,
    valueFormatter: formatters.dollarsFormatterWithCents,
    filter: "agNumberColumnFilter",
    type: "numericColumn",
    width: 120,
  },
  {
    headerName: "Method",
    field: "paymentMethod",
    width: 120,
  },
  {
    headerName: "Ref",
    field: "paymentRef",
    flex: 1,
    minWidth: 120,
  },
  {
    headerName: "Notes",
    field: "notes",
    flex: 2,
    minWidth: 180,
  },
];

// This column is added only when useShowBillingDocumentField returns true.
// Other deployments never set billing_document_id. There, the column
// would show every payment as unassigned.
const billingDocumentColumnDef = {
  headerName: "Billing Document",
  field: "billingDocumentId",
  cellRenderer: BillingDocumentColumnRenderer,
  minWidth: 260,
  flex: 1,
};

const actionColumnDef = {
  headerName: ACTION_COLUMN_NAME,
  cellRenderer: ActionsColumnRenderer,
  pinned: "right",
  width: 100,
};

const selectBusinessPaymentsList = createSelector(
  [getPayments, (_, businessId) => businessId],
  (payments, businessId) =>
    Object.values(payments).filter(
      payment => payment.fromBusinessId === businessId,
    ),
);

function BusinessPaymentsTableComponent(props) {
  const { businessId } = props;

  const rowData = useSelector(state =>
    selectBusinessPaymentsList(state, businessId),
  );
  const showBillingDocumentColumn = useShowBillingDocumentField();
  useEnsureBillingRunLoaded(showBillingDocumentColumn);

  const columnDefs = useMemo(
    () => [
      ...baseColumnsDefs,
      ...(showBillingDocumentColumn ? [billingDocumentColumnDef] : []),
      actionColumnDef,
    ],
    [showBillingDocumentColumn],
  );

  return (
    <AgGrid
      rowData={rowData}
      suppressColumnVirtualisation
      suppressScrollOnNewData
      columnDefs={columnDefs}
      rowBuffer={50}
      defaultColDef={defaultColDef}
      getRowId={getRowId}
    />
  );
}

export const BusinessPaymentsTable = memo(BusinessPaymentsTableComponent);
BusinessPaymentsTable.propTypes = {
  businessId: PropTypes.string.isRequired,
};
